import type { SearchedLoan } from '@/api/models/loan/SearchedLoan'
import ConversionUtility from '@/utils/ConversionUtility'

/**
 * Calculates total pending interest for a loan on a daily basis
 * from the loan start date until the current date,
 * accounting for each EMI transaction.
 *
 * @param loan - The loan object with details and payment history.
 * @param currentDate - The date (in epoch ms) when customer is paying.
 * @returns Total interest that needs to be paid.
 */
export default function calculateDailyPendingInterest(loan: SearchedLoan | undefined, currentDate: number): number {
  if (!loan) return 0
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const { interestRate, emiTransactions, loanStartDate } = loan;
  const dailyInterestRate = interestRate / 365 / 100;

  // Sort EMI transactions by dueDate
  const transactions = [...emiTransactions].sort((a, b) => a.dueDate - b.dueDate);

  let remainingPrincipal = loan.loanAmount;
  let totalPendingInterest = 0;
  let lastDate = loanStartDate;

  for (const txn of transactions) {
    const txnDate = txn.createdAt;

    if (txnDate > currentDate) break; // Ignore future payments

    // Days between last date and this payment
    const daysDiff = Math.floor((txnDate - lastDate) / MS_PER_DAY);
    if (daysDiff > 0) {
      const accruedInterest = remainingPrincipal * dailyInterestRate * daysDiff;
      totalPendingInterest += accruedInterest;
    }

    // Reduce pending interest if paid
    totalPendingInterest -= txn.interestPaid;
    if (totalPendingInterest < 0) totalPendingInterest = 0; // No negative interest

    // Reduce principal
    remainingPrincipal -= txn.principalPaid;
    lastDate = txnDate;
  }

  // Interest from last payment date till current date
  const daysSinceLast = Math.floor((currentDate - lastDate) / MS_PER_DAY);
  if (daysSinceLast > 0) {
    const interestSinceLast = remainingPrincipal * dailyInterestRate * daysSinceLast;
    totalPendingInterest += interestSinceLast;
  }

  return ConversionUtility.toRuppesCoversion(Number(totalPendingInterest.toFixed(0)));
}
